import React, { ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  deleteNotification,
  fetchAllNotifications,
  readNotification,
} from '../../redux/api/notifications';
import _ from 'lodash';
import { toast } from '../../components/toast';

export interface NotificationItem {
  id: string;
  title: string;
  link?: string;
  text: string;
  read: boolean;
  created_at: string;
  image?: {
    id: number;
    large: string;
    medium: string;
    original: string;
    small: string;
  };
  applications?: string;
  post_date: string;
}

interface NotificationsContextData {
  notifications: NotificationItem[];
  handleReadNotification: (id: string) => void;
  isLoading: boolean;
  getNotifications: (page: number) => void;
  finishedNotificationsList: boolean;
  handleDeleteNotification: (ids: string[]) => void;
}

interface NotificationsProviderProps {
  isReport: boolean;
  children: ReactNode;
}

export const NotificationsContext = createContext<NotificationsContextData>(
  {} as NotificationsContextData
);

const NotificationsProvider = ({ isReport, children }: NotificationsProviderProps) => {
  const [finishedNotificationsList, setFinishedNotificationsList] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const firstTime = useRef(true);

  useEffect(() => {
    if (!isReport) {
      getNotifications(1);
    }
    // eslint-disable-next-line
  }, [isReport]);

  useEffect(() => {
    if (!isReport) {
      const interval = setInterval(() => {
        getNotifications(1);
      }, 10 * 60 * 1000);

      return () => clearInterval(interval);
    }
    // eslint-disable-next-line
  }, [isReport]);

  function sortNotifications(notifications: NotificationItem[]): NotificationItem[] {
    return notifications.sort((a, b) => {
      if ((a.read === null || a.read === false) && b.read !== null && b.read !== false) {
        return -1;
      } else if (a.read !== null && a.read !== false && (b.read === null || b.read === false)) {
        return 1;
      }

      const dateA = new Date(a.post_date);
      const dateB = new Date(b.post_date);

      return dateB.getTime() - dateA.getTime();
    });
  }

  const verifyNotificationNew = (
    prevNotifications: NotificationItem[],
    resultsNotifications: NotificationItem[],
    page?: number
  ) => {
    if (!firstTime.current && page === 1) {
      const existingNotificationIds = new Set(prevNotifications.map((n) => n.id));

      const newNotifications = resultsNotifications.filter(
        (notification: NotificationItem) => !existingNotificationIds.has(notification.id)
      );

      if (newNotifications.length > 0) {
        toast({ message: 'Você tem novas notificações', type: 'info' });
      }
    } else {
      firstTime.current = false;
    }
  };

  const getNotifications = async (page: number) => {
    if (window.location.pathname === '/auth/login') return;

    setIsLoading(true);
    try {
      const resultsNotifications = await fetchAllNotifications({ page });
      if (resultsNotifications.length === 0 && !firstTime.current)
        setFinishedNotificationsList(true);

      setNotifications((prevNotifications) => {
        verifyNotificationNew(prevNotifications, resultsNotifications, page);
        const concatNotifications = _.concat(prevNotifications, resultsNotifications);
        const removeDuplicated = _.uniqBy(concatNotifications, 'id');
        const notificationsSorted = sortNotifications(removeDuplicated);

        return notificationsSorted;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReadNotification = async (id: string) => {
    try {
      await readNotification(id);
      setNotifications((state) =>
        state.map((notification) => {
          if (notification.id === id) {
            return { ...notification, read: true };
          }
          return notification;
        })
      );
      toast({ message: 'Notificação marcada como lida', type: 'success' });
    } catch (e) {
      console.log(e);
    }
  };

  const handleDeleteNotification = async (ids: string[]) => {
    try {
      deleteNotification(ids);
      setNotifications((state) => state.filter((notification) => !ids.includes(notification.id)));
      toast({ message: 'Notificações deletadas com sucesso', type: 'success' });
    } catch (e) {
      console.log(e);
      toast({ message: 'Erro ao deletar notificações', type: 'error' });
    }
  };

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        handleReadNotification,
        isLoading,
        getNotifications,
        finishedNotificationsList,
        handleDeleteNotification,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

export default NotificationsProvider;
